import React from 'react';
import { Tabs, Typography, Modal, Input, message, Tooltip } from 'antd';
import { Download, Mail, FileDown, Video, Image } from 'lucide-react';
import { IconPill } from '@doer/frontend-components/icon-pill';
import { palette } from '@doer/frontend-components/theme';
import { CreativityContentData } from '../../../types/banner';
import { MediaPreview } from './media-preview';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';
import { addCacheBusting, getAllImages, getAllVideos } from '../../../utils';
import { useDownloadMedia } from '../../../hooks/media/use-download-media';
import { useSendMediaEmail } from '../../../hooks/media/use-send-media';

const { Text } = Typography;

interface AssetMediaPanelProps {
  creativity: CreativityContentData;
}

export const AssetMediaPanel: React.FC<AssetMediaPanelProps> = ({ creativity }) => {
  const { fontSize, spacing } = useResponsiveSizes();
  const { downloadBannerZip } = useDownloadMedia();
  const { sendMediaEmail } = useSendMediaEmail();

  const videos = getAllVideos(creativity);
  const images = getAllImages(creativity);

  const getInitialTab = (): 'video' | 'image' => {
    const hasVideos = videos.length > 0;
    const hasImages = images.length > 0;

    if (hasVideos) return 'video';
    if (hasImages) return 'image';

    return 'image';
  };

  const [activeTab, setActiveTab] = React.useState<'video' | 'image'>(getInitialTab());
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isSendingEmail, setIsSendingEmail] = React.useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = React.useState(false);
  const [emailInput, setEmailInput] = React.useState('');
  const [emailError, setEmailError] = React.useState('');

  const videoUrlsAndFormats = videos.map(video => ({
    url: addCacheBusting(video.url, creativity.updatedAt ?? creativity.date),
    format: video.format
  }));
  const imageUrlsAndFormats = images.map(img => ({
    url: addCacheBusting(img.url, creativity.updatedAt ?? creativity.date),
    format: img.format
  }));

  const hasVideo = videoUrlsAndFormats.length > 0;
  const hasImage = imageUrlsAndFormats.length > 0;
  const hasContent = hasVideo || hasImage;

  const handleDownloadAll = async () => {
    if (!creativity.bannerId) return;

    setIsDownloading(true);
    try {
      await downloadBannerZip(creativity.bannerId);
      message.success('Descarga iniciada');
    } catch (error) {
      console.error('Error al generar descarga:', error);
      message.error('Error al generar la descarga');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!creativity.bannerId) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailInput.trim()) {
      setEmailError('Por favor ingresa un email');
      return;
    }
    if (!emailRegex.test(emailInput)) {
      setEmailError('Email inválido');
      return;
    }

    setIsSendingEmail(true);
    setIsEmailModalOpen(false);

    try {
      await sendMediaEmail(creativity.bannerId, emailInput.trim());
      message.success('Email enviado correctamente');
      setEmailInput('');
    } catch (error) {
      console.error('Error al enviar el email:', error);
      message.error('Error al enviar el email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleOpenEmailModal = () => {
    setEmailInput('');
    setEmailError('');
    setIsEmailModalOpen(true);
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
    setEmailInput('');
    setEmailError('');
  };

  if (!hasContent) {
    return (
      <div
        style={{
          color: '#64748b',
          alignItems: 'center',
          justifyContent: 'center',
          padding: spacing.xl,
          display: 'flex',
          alignContent: 'center',
          justifyItems: 'center',
          height: '100%'
        }}
      >
        <Text style={{ color: '#64748b', fontSize: fontSize.lg }}>No hay contenido disponible para mostrar</Text>
      </div>
    );
  }

  const getScrollStyle = (count: number): React.CSSProperties =>
    count > 1
      ? {
          flex: 1,
          maxHeight: 'calc(85vh - 250px)',
          overflowY: 'auto',
          scrollbarColor: '#e6e6e6 #FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }
      : {
          height: '85vh',
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: spacing.md }}>
      <Tabs
        activeKey={activeTab}
        onChange={key => setActiveTab(key as 'video' | 'image')}
        size='small'
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        tabBarExtraContent={
          <div style={{ padding: '0 10px', display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
            <Tooltip title='Descargar todo'>
              <IconPill onClick={isDownloading ? undefined : handleDownloadAll}>
                <Download size={20} />
              </IconPill>
            </Tooltip>

            <Tooltip title='Enviar por email'>
              <IconPill onClick={isSendingEmail ? undefined : handleOpenEmailModal}>
                <Mail size={20} />
              </IconPill>
            </Tooltip>

            {hasVideo && activeTab === 'video' && (
              <Tooltip title='Configuración pendiente'>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.xs,
                    color: palette.primitives.grey.grey300,
                    fontSize: fontSize.lg,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    padding: 8,
                    opacity: 0.6
                  }}
                >
                  <FileDown size={20} />
                  VAST
                </div>
              </Tooltip>
            )}
          </div>
        }
        items={[
          {
            key: 'video',
            label: (
              <IconPill selected={activeTab === 'video'}>
                <Video size={20} />
              </IconPill>
            ),
            disabled: !hasVideo,
            children: hasVideo ? (
              <div style={getScrollStyle(videoUrlsAndFormats.length)}>
                {videoUrlsAndFormats.map((item, idx) => (
                  <MediaPreview key={idx} type='video' src={item.url} format={item.format} />
                ))}
              </div>
            ) : null
          },
          {
            key: 'image',
            label: (
              <IconPill selected={activeTab === 'image'}>
                <Image size={20} />
              </IconPill>
            ),
            disabled: !hasImage,
            children: hasImage ? (
              <div style={getScrollStyle(imageUrlsAndFormats.length)}>
                {imageUrlsAndFormats.map((item, idx) => (
                  <MediaPreview key={idx} type='image' src={item.url} format={item.format} />
                ))}
              </div>
            ) : null
          }
        ]}
      />

      <Modal
        title='Enviar asset por email'
        open={isEmailModalOpen}
        onOk={handleSendEmail}
        onCancel={handleCloseEmailModal}
        okText='Enviar'
        cancelText='Cancelar'
        confirmLoading={isSendingEmail}
        width={400}
      >
        <div style={{ padding: '10px 0' }}>
          <Text style={{ marginBottom: 8, display: 'block' }}>Ingresa el email donde queres recibir tu asset</Text>
          <Input
            placeholder='user@drupi.io'
            value={emailInput}
            onChange={e => {
              setEmailInput(e.target.value);
              setEmailError('');
            }}
            onPressEnter={handleSendEmail}
            status={emailError ? 'error' : ''}
            size='small'
          />
          {emailError && (
            <Text type='danger' style={{ fontSize: fontSize.lg, marginTop: 4, display: 'block' }}>
              {emailError}
            </Text>
          )}
        </div>
      </Modal>
    </div>
  );
};
